import type { MarbleRaceEngine } from './core';
import { activeRaceEventAt } from './events';
import {
  beltIsActive,
  fieldContours,
  fieldGateIsClosed,
  fieldPinIsRaised,
  liftTravelPose,
  pumpDirection,
  type FieldShape,
  type FieldSpec,
} from './field';
import { PATH_GATE_EVENT_SHIFT, pointOnPath, trackSamples } from './maps';
import { gravityFlowTarget } from './gravity-flow';
import { marbleDisplayName } from './input';
import { MARBLE_RADIUS } from './constants';
import { createMiniMapLayout } from './minimap';
import type { ActiveRaceEvent } from './events';
import type { CameraTargetSelection, CameraView, FateMapDefinition, MapObstacle, MapZone, RaceMarble } from './types';

const FIELD_FLOOR = '#0d1330';
const FIELD_WALL = '#232c58';
const FIELD_EDGE = '#39418c';
const FIELD_OPEN = '#3ef2b6';
const FIELD_CLOSED = '#ff7a70';

export interface FieldPipeSegment {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
  readonly width: number;
}

export function nameLabelIds(selection: CameraTargetSelection): readonly string[] {
  return selection.highlightedIds.slice(0, 1);
}

export function fieldPipeRenderSegments(shape: Extract<FieldShape, { kind: 'pipe' }>): FieldPipeSegment[] {
  const cumulative: number[] = [0];
  for (let index = 1; index < shape.points.length; index += 1) {
    const previous = shape.points[index - 1];
    const point = shape.points[index];
    cumulative.push(cumulative[index - 1] + Math.hypot(point.x - previous.x, point.y - previous.y));
  }
  const total = Math.max(1, cumulative[cumulative.length - 1]);
  const endWidth = shape.endWidth ?? shape.width;
  const segments: FieldPipeSegment[] = [];
  for (let index = 0; index < shape.points.length - 1; index += 1) {
    const start = shape.points[index];
    const end = shape.points[index + 1];
    const startFraction = cumulative[index] / total;
    const endFraction = cumulative[index + 1] / total;
    const startWidth = shape.width + (endWidth - shape.width) * startFraction;
    const finishWidth = shape.width + (endWidth - shape.width) * endFraction;
    // Canvas 선은 구간별 고정 폭이므로, 테이퍼 폭 차이가 3px 이하가 되게 나눠
    // SDF 충돌 윤곽과 보이는 벽의 오차를 반경 1.5px 이내로 유지한다.
    const subdivisions = Math.max(1, Math.ceil(Math.abs(finishWidth - startWidth) / 3));
    for (let part = 0; part < subdivisions; part += 1) {
      const from = part / subdivisions;
      const to = (part + 1) / subdivisions;
      const middle = (from + to) / 2;
      segments.push({
        x0: start.x + (end.x - start.x) * from,
        y0: start.y + (end.y - start.y) * from,
        x1: start.x + (end.x - start.x) * to,
        y1: start.y + (end.y - start.y) * to,
        width: startWidth + (finishWidth - startWidth) * middle,
      });
    }
  }
  return segments;
}

interface FieldLayoutPaths {
  readonly solids: Path2D;
  readonly pipeSegments: readonly FieldPipeSegment[];
  readonly pipePaths: readonly { width: number; path: Path2D }[];
  readonly holes: Path2D;
  readonly contour?: boolean;
}

const fieldLayoutCache = new WeakMap<FieldSpec, FieldLayoutPaths>();

function shapeToPath(path: Path2D, shape: FieldShape): void {
  if (shape.kind === 'room') {
    const radius = Math.min(shape.r ?? 18, shape.w / 2, shape.h / 2);
    path.roundRect(shape.x, shape.y, shape.w, shape.h, radius);
  } else if (shape.kind === 'disc') {
    path.moveTo(shape.x + shape.r, shape.y);
    path.arc(shape.x, shape.y, shape.r, 0, Math.PI * 2);
  } else if (shape.kind === 'ellipse') {
    path.moveTo(shape.x + shape.rx, shape.y);
    path.ellipse(shape.x, shape.y, shape.rx, shape.ry, 0, 0, Math.PI * 2);
  } else if (shape.kind === 'poly') {
    shape.points.forEach((point, index) => {
      if (index === 0) path.moveTo(point.x, point.y);
      else path.lineTo(point.x, point.y);
    });
    path.closePath();
  }
}

function fieldLayoutPaths(spec: FieldSpec, contourMap?: FateMapDefinition): FieldLayoutPaths {
  const cached = fieldLayoutCache.get(spec);
  if (cached) return cached;
  const solids = new Path2D();
  if (contourMap) {
    // 서로 겹친 사발/깔때기의 내부 선을 벽처럼 그리지 않도록 실제 충돌 윤곽을 쓴다.
    for (const loop of fieldContours(spec, contourMap.worldWidth, contourMap.worldHeight)) {
      loop.forEach((point, index) => index === 0 ? solids.moveTo(point.x, point.y) : solids.lineTo(point.x, point.y));
      solids.closePath();
    }
    const layout = { solids, pipeSegments: [], pipePaths: [], holes: new Path2D(), contour: true };
    fieldLayoutCache.set(spec, layout);
    return layout;
  }
  const pipeSegments: FieldPipeSegment[] = [];
  for (const shape of spec.shapes) {
    if (shape.kind !== 'pipe') {
      shapeToPath(solids, shape);
      continue;
    }
    pipeSegments.push(...fieldPipeRenderSegments(shape));
  }
  const holes = new Path2D();
  for (const hole of spec.holes) shapeToPath(holes, hole);
  // Opaque floor/wall strokes can share a path without changing their silhouette.
  const paths = new Map<number, Path2D>();
  for (const segment of pipeSegments) {
    const path = paths.get(segment.width) ?? new Path2D();
    path.moveTo(segment.x0, segment.y0);
    path.lineTo(segment.x1, segment.y1);
    paths.set(segment.width, path);
  }
  const layout: FieldLayoutPaths = { solids, pipeSegments, holes, pipePaths: [...paths].map(([width, path]) => ({ width, path })) };
  fieldLayoutCache.set(spec, layout);
  return layout;
}

export interface FateRenderOptions {
  readonly cameraSelection: CameraTargetSelection;
  readonly cameraInspecting: boolean;
  readonly resultMarbleId?: string;
  readonly reduceMotion: boolean;
}

/** 결승 판정을 받은 마블은 트랙과 미니맵에서 즉시 퇴장한다. */
export function marblesStillOnTrack(marbles: readonly RaceMarble[]): RaceMarble[] {
  return marbles.filter((marble) => marble.finishedAt === null);
}

export function obstacleRenderPose(
  engine: MarbleRaceEngine,
  obstacle: MapObstacle,
  active: ActiveRaceEvent | null,
) {
  const gateShift = active?.event.type === 'gate'
    ? (obstacle.lateral >= 0 ? -1 : 1) * PATH_GATE_EVENT_SHIFT * active.envelope
    : 0;
  const lateral = obstacle.type === 'gate' ? obstacle.lateral + gateShift : obstacle.lateral;
  const pose = pointOnPath(engine.map.path, obstacle.progress, lateral);
  const replayAngle = obstacle.type === 'spinner' ? engine.spinnerAngle(obstacle.id) : null;
  const reverseOffset = active?.event.type === 'reverse' ? active.envelope * Math.PI * 1.2 : 0;
  const rotation = replayAngle ?? pose.angle + (obstacle.phase ?? 0)
    + engine.elapsed * (obstacle.speed ?? 0)
    - reverseOffset * Math.sign(obstacle.speed ?? 1);
  return { ...pose, rotation };
}

const marbleNameSegmenter = typeof Intl.Segmenter === 'function'
  ? new Intl.Segmenter('ko-KR', { granularity: 'grapheme' })
  : null;
const abbreviatedNameCache = new Map<string, string>();
const innerNameCache = new Map<string, string>();
const innerNameSpriteCache = new Map<string, HTMLCanvasElement>();

export function abbreviatedMarbleName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const cached = abbreviatedNameCache.get(trimmed);
  if (cached) return cached;
  const first = marbleNameSegmenter
    ? marbleNameSegmenter.segment(trimmed)[Symbol.iterator]().next().value?.segment
    : Array.from(trimmed)[0];
  const abbreviated = first ?? '?';
  abbreviatedNameCache.set(trimmed, abbreviated);
  return abbreviated;
}

function marbleNameSprite(label: string): HTMLCanvasElement {
  const cached = innerNameSpriteCache.get(label);
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = 56;
  canvas.height = 36;
  const context = canvas.getContext('2d')!;
  context.scale(2, 2);
  context.font = '900 8px "Noto Sans KR", sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.lineJoin = 'round';
  context.lineWidth = 2.2;
  context.strokeStyle = 'rgba(255,255,255,.78)';
  context.strokeText(label, 14, 9.4, 16);
  context.fillStyle = '#241d38';
  context.fillText(label, 14, 9.4, 16);
  innerNameSpriteCache.set(label, canvas);
  return canvas;
}

function pathToContext(context: CanvasRenderingContext2D, map: FateMapDefinition): void {
  context.beginPath();
  trackSamples(map.path).forEach((point, index) => {
    if (index === 0) context.moveTo(point.x, point.y);
    else context.lineTo(point.x, point.y);
  });
}

interface TrackStrokeBucket {
  readonly width: number;
  readonly path: Path2D;
}

const trackStrokeCache = new WeakMap<FateMapDefinition, Map<number, readonly TrackStrokeBucket[]>>();

function trackStrokeBuckets(map: FateMapDefinition, padding: number): readonly TrackStrokeBucket[] {
  const mapCache = trackStrokeCache.get(map) ?? new Map<number, readonly TrackStrokeBucket[]>();
  const cached = mapCache.get(padding);
  if (cached) return cached;
  const pathsByWidth = new Map<number, Path2D>();
  const samples = trackSamples(map.path);
  for (let index = 1; index < samples.length; index += 1) {
    const previous = samples[index - 1];
    const point = samples[index];
    const width = Math.max(10, Math.round(((previous.width + point.width) / 2 + padding * 2) / 4) * 4);
    const path = pathsByWidth.get(width) ?? new Path2D();
    path.moveTo(previous.x, previous.y);
    path.lineTo(point.x, point.y);
    pathsByWidth.set(width, path);
  }
  const buckets = [...pathsByWidth].map(([width, path]) => ({ width, path }));
  mapCache.set(padding, buckets);
  trackStrokeCache.set(map, mapCache);
  return buckets;
}

function strokeVariableTrack(
  context: CanvasRenderingContext2D,
  map: FateMapDefinition,
  padding: number,
  color: string,
): void {
  context.save();
  context.strokeStyle = color;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  for (const bucket of trackStrokeBuckets(map, padding)) {
    context.lineWidth = bucket.width;
    context.stroke(bucket.path);
  }
  context.restore();
}

const ZONE_COLORS: Record<MapZone['type'], string> = {
  boost: '#42e8ff',
  slow: '#ff7eb3',
  collision: '#ffd166',
  finish: '#fff3a3',
};

function isInsideView(x: number, y: number, camera: CameraView, width: number, height: number): boolean {
  const screenX = width / 2 + (x - camera.x) * camera.zoom;
  const screenY = height / 2 + (y - camera.y) * camera.zoom;
  return screenX > -100 && screenX < width + 100 && screenY > -60 && screenY < height + 60;
}

export class FateMarbleRenderer {
  private readonly context: CanvasRenderingContext2D;
  private miniMapTrack: { map: FateMapDefinition; width: number; height: number; compact: boolean; canvas: HTMLCanvasElement } | null = null;
  width = 960;
  height = 600;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas를 사용할 수 없습니다.');
    this.context = context;
  }

  resize(marbleCount: number): void {
    const bounds = this.canvas.getBoundingClientRect();
    this.width = Math.max(320, Math.round(bounds.width || 960));
    this.height = Math.max(260, Math.round(bounds.height || 600));
    const ratioLimit = marbleCount > 60 ? 1.25 : 2;
    const ratio = Math.min(ratioLimit, window.devicePixelRatio || 1);
    const pixelWidth = Math.round(this.width * ratio);
    const pixelHeight = Math.round(this.height * ratio);
    if (this.canvas.width !== pixelWidth || this.canvas.height !== pixelHeight) {
      this.canvas.width = pixelWidth;
      this.canvas.height = pixelHeight;
    }
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  draw(engine: MarbleRaceEngine, camera: CameraView, options: FateRenderOptions): void {
    const context = this.context;
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    const pixelRatio = this.canvas.width / this.width;
    context.scale(pixelRatio, pixelRatio);
    context.fillStyle = engine.map.background;
    context.fillRect(0, 0, this.width, this.height);
    const glow = context.createRadialGradient(this.width * .5, this.height * .38, 20, this.width * .5, this.height * .38, this.width * .7);
    glow.addColorStop(0, `${engine.map.accent}24`);
    glow.addColorStop(1, 'transparent');
    context.fillStyle = glow;
    context.fillRect(0, 0, this.width, this.height);

    context.save();
    context.translate(this.width / 2 - camera.x * camera.zoom, this.height / 2 - camera.y * camera.zoom);
    context.scale(camera.zoom, camera.zoom);
    if (engine.map.field) this.drawFieldWorld(engine, camera, options);
    else this.drawWorld(engine, camera, options);
    context.restore();
    this.drawMiniMap(engine, camera, options.cameraSelection, options.cameraInspecting);
    context.restore();
  }

  private drawWorld(engine: MarbleRaceEngine, camera: CameraView, options: FateRenderOptions): void {
    const context = this.context;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    const active = activeRaceEventAt(engine.events, engine.elapsed);

    context.save();
    context.globalAlpha = .1;
    context.fillStyle = engine.map.accent;
    for (let y = 80; y < engine.map.worldHeight; y += 180) {
      for (let x = 70 + (y / 180 % 2) * 80; x < engine.map.worldWidth; x += 250) {
        context.beginPath();
        context.arc(x, y, 5, 0, Math.PI * 2);
        context.fill();
      }
    }
    context.restore();

    this.drawTrack(engine.map, options.reduceMotion);
    this.drawZones(engine.map, active);
    this.drawLandmarks(engine.map);
    for (const obstacle of engine.map.obstacles) this.drawObstacle(engine, obstacle, active);

    this.drawLine(engine.map, 0, 'START', '#70e6d4');
    this.drawLine(engine.map, 1, 'FINISH', engine.map.accent);

    if (active && !options.reduceMotion) {
      context.save();
      context.globalAlpha = .06 + active.envelope * .08;
      context.strokeStyle = engine.map.accent;
      context.lineWidth = 12;
      pathToContext(context, engine.map);
      context.stroke();
      context.restore();
    }

    this.drawMarblePass(engine, camera, options);
  }

  private drawMarblePass(engine: MarbleRaceEngine, camera: CameraView, options: FateRenderOptions): void {
    const orderedForDrawing = marblesStillOnTrack(engine.marbles)
      .sort((left, right) => left.progress - right.progress);
    const visibleForDrawing = orderedForDrawing.filter((marble) => (
      isInsideView(marble.x, marble.y, camera, this.width, this.height)
    ));
    if (!options.reduceMotion) {
      for (const marble of visibleForDrawing) this.drawMotionTrail(engine.map, marble);
    }
    for (const marble of visibleForDrawing) {
      this.drawMarble(marble, marble.x, marble.y, camera.highlightedIds.has(marble.id), options.resultMarbleId === marble.id);
    }
    this.drawCollisionEffects(engine, options.reduceMotion, camera.zoom);

    const labelIds = new Set(nameLabelIds(options.cameraSelection));
    let labelIndex = 0;
    for (const marble of visibleForDrawing) {
      if (labelIds.has(marble.id)) {
        this.drawNameLabel(marble, marble.x, marble.y, camera.zoom, labelIndex);
        labelIndex += 1;
      }
    }
  }

  private drawFieldWorld(engine: MarbleRaceEngine, camera: CameraView, options: FateRenderOptions): void {
    const context = this.context;
    const spec = engine.map.field!;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    const active = activeRaceEventAt(engine.events, engine.elapsed);

    context.save();
    context.globalAlpha = .08;
    context.fillStyle = engine.map.accent;
    for (let y = 80; y < engine.map.worldHeight; y += 180) {
      for (let x = 70 + (y / 180 % 2) * 80; x < engine.map.worldWidth; x += 250) {
        context.beginPath();
        context.arc(x, y, 5, 0, Math.PI * 2);
        context.fill();
      }
    }
    context.restore();

    this.drawFieldLayout(spec, engine.map.id === 'tower' ? engine.map : undefined);
    this.drawFieldZones(engine, spec, active);
    this.drawFieldMarkers(engine, spec);
    this.drawFieldObstacles(engine, spec, active);
    this.drawMarblePass(engine, camera, options);
  }

  private drawFieldLayout(spec: FieldSpec, contourMap?: FateMapDefinition): void {
    const context = this.context;
    const layout = fieldLayoutPaths(spec, contourMap);
    const passes: Array<{ color: string; expand: number }> = [
      { color: FIELD_WALL, expand: 18 },
      { color: FIELD_EDGE, expand: 9 },
      { color: FIELD_FLOOR, expand: 0 },
    ];
    for (const pass of passes) {
      context.fillStyle = pass.color;
      context.strokeStyle = pass.color;
      if (pass.expand > 0) {
        context.lineWidth = pass.expand;
        context.stroke(layout.solids);
      }
      context.fill(layout.solids, layout.contour ? 'evenodd' : 'nonzero');
      for (const pipe of layout.pipePaths) {
        context.lineWidth = pipe.width + pass.expand;
        context.stroke(pipe.path);
      }
    }
    context.fillStyle = FIELD_WALL;
    context.strokeStyle = FIELD_EDGE;
    context.lineWidth = 5;
    context.fill(layout.holes);
    context.stroke(layout.holes);
  }

  private drawFieldZones(engine: MarbleRaceEngine, spec: FieldSpec, active: ActiveRaceEvent | null): void {
    const context = this.context;
    const accent = engine.map.accent;
    // 벨트 셰브런 — 방향은 애니메이션으로만 표현 (역주 이벤트 시 반전).
    // 대각 벨트는 캡슐 축, 세로 성분 벨트는 세로 중앙선을 따라 흐른다.
    for (const zone of spec.zones) {
      if (zone.kind !== 'belt' || zone.chevrons === false) continue;
      const eventActive = zone.eventType !== undefined && active?.event.type === zone.eventType;
      if (!beltIsActive(zone, engine.elapsed, eventActive)) continue;
      const rect = eventActive && zone.eventRect ? zone.eventRect : zone.rect;
      const reversing = zone.reversible !== false && active?.event.type === 'belt-reverse' ? 1 - 2 * active.envelope : 1;
      const beltVy = zone.vy ?? 0;
      let x0: number; let y0: number; let x1: number; let y1: number;
      if (zone.capsule) {
        const points = zone.capsule.points;
        x0 = points[0].x; y0 = points[0].y;
        x1 = points[points.length - 1].x; y1 = points[points.length - 1].y;
      } else if (Math.abs(zone.vx) >= Math.abs(beltVy)) {
        x0 = rect.x + 20; y0 = rect.y + rect.h / 2;
        x1 = rect.x + rect.w - 20; y1 = y0;
      } else {
        x0 = rect.x + rect.w / 2; y0 = rect.y + 20;
        x1 = x0; y1 = rect.y + rect.h - 20;
      }
      const span = Math.max(1, Math.hypot(x1 - x0, y1 - y0));
      const unitX = (x1 - x0) / span;
      const unitY = (y1 - y0) / span;
      const forward = Math.sign((zone.vx * unitX + beltVy * unitY) * reversing) || 1;
      const spacing = 52;
      const offset = (engine.elapsed * Math.hypot(zone.vx, beltVy) * .55) % spacing * forward;
      const dashLength = (24 - zone.lane * 6) * forward;
      context.save();
      context.strokeStyle = zone.tint ?? accent;
      context.globalAlpha = (.95 - zone.lane * .16) * .9;
      if (zone.tint) {
        // 역주행 패치 — 작동 영역을 점선 테두리로 표시
        context.lineWidth = 2.5;
        context.setLineDash([9, 7]);
        context.strokeRect(rect.x + 3, rect.y + 3, rect.w - 6, rect.h - 6);
        context.setLineDash([]);
      }
      context.lineWidth = 4;
      context.beginPath();
      const usable = Math.max(spacing, span - 12);
      for (let along = 12; along < span - 12; along += spacing) {
        const marched = ((along + offset) % usable + usable) % usable;
        const dashX = x0 + unitX * marched;
        const dashY = y0 + unitY * marched;
        context.moveTo(dashX, dashY);
        context.lineTo(dashX + unitX * dashLength, dashY + unitY * dashLength);
      }
      context.stroke();
      context.restore();
    }
    // 역류 펌프 — 분사 방향이 반 주기마다 좌우로 뒤집힌다
    for (const zone of spec.zones) {
      if (zone.kind !== 'pump') continue;
      const side = pumpDirection(zone, engine.elapsed);
      const magnitude = Math.max(1, Math.hypot(zone.ax, zone.ay));
      const directionX = zone.ax / magnitude * side;
      const directionY = zone.ay / magnitude * side;
      const centerX = zone.rect.x + zone.rect.w / 2;
      const centerY = zone.rect.y + zone.rect.h / 2;
      const originX = centerX - directionX * zone.rect.w * .36;
      const originY = centerY - directionY * zone.rect.h * .36;
      context.save();
      context.strokeStyle = '#ff5ca8';
      context.lineCap = 'round';
      context.lineWidth = 4.5;
      context.globalAlpha = .92;
      for (let jet = 0; jet < 3; jet += 1) {
        const lateral = (jet - 1) * 24;
        const baseX = originX - directionY * lateral;
        const baseY = originY + directionX * lateral;
        context.beginPath();
        context.moveTo(baseX - directionX * 8 - directionY * 9, baseY - directionY * 8 + directionX * 9);
        context.lineTo(baseX + directionX * 10, baseY + directionY * 10);
        context.lineTo(baseX - directionX * 8 + directionY * 9, baseY - directionY * 8 - directionX * 9);
        context.stroke();
      }
      context.restore();
    }
    // 순환 사발: 물리와 같은 진행도 함수로 회랑의 중력 방향을 표시한다.
    if (spec.gravityFlow) {
      const flow = spec.gravityFlow;
      for (let index = 0; index < flow.stations.length - 1; index += 1) {
        const from = flow.stations[index];
        const to = flow.stations[index + 1];
        const t = .38 + ((engine.elapsed * .18 + index * .23) % 1) * .24;
        const x = from.x + (to.x - from.x) * t;
        const y = from.y + (to.y - from.y) * t;
        const gravity = gravityFlowTarget(flow, from.progress + (to.progress - from.progress) * t, x, y);
        context.save();
        context.translate(x, y);
        context.rotate(Math.atan2(gravity.y, gravity.x));
        context.strokeStyle = '#ffd449';
        context.globalAlpha = .8;
        context.lineWidth = 3.5;
        context.lineCap = 'round';
        context.beginPath();
        context.moveTo(-28, 0); context.lineTo(28, 0);
        context.moveTo(17, -9); context.lineTo(28, 0); context.lineTo(17, 9);
        context.stroke();
        context.restore();
      }
    }
    for (const zone of spec.zones) {
      if (zone.kind !== 'wait' || !zone.circle || !zone.calm || !zone.eventType) continue;
      const released = active?.event.type === zone.eventType ? active.envelope : 0;
      context.save();
      context.strokeStyle = '#ffd449';
      context.fillStyle = '#ffd449';
      context.globalAlpha = .06 * (1 - released);
      context.beginPath();
      context.arc(zone.circle.x, zone.circle.y, zone.circle.r, 0, Math.PI * 2);
      context.fill();
      context.globalAlpha = .75 * (1 - released) + .1;
      context.lineWidth = 3;
      context.setLineDash([10, 8]);
      context.lineDashOffset = -engine.elapsed * 10;
      context.stroke();
      context.restore();
    }
    // 중력 존 화살표 (라벨이 있는 방만)
    for (const zone of spec.zones) {
      if (zone.kind !== 'gravity' || !zone.label) continue;
      const magnitude = Math.max(1, Math.hypot(zone.gx, zone.gy));
      const directionX = zone.gx / magnitude;
      const directionY = zone.gy / magnitude;
      const centerX = zone.rect.x + Math.min(84, zone.rect.w * .22);
      const centerY = zone.rect.y + Math.min(96, zone.rect.h * .34);
      context.save();
      context.strokeStyle = '#4e8aff';
      context.globalAlpha = .85;
      context.lineWidth = 4;
      context.lineCap = 'round';
      context.beginPath();
      context.moveTo(centerX - directionX * 24, centerY - directionY * 24);
      context.lineTo(centerX + directionX * 24, centerY + directionY * 24);
      const tipX = centerX + directionX * 24;
      const tipY = centerY + directionY * 24;
      context.moveTo(tipX, tipY);
      context.lineTo(tipX - directionX * 11 - directionY * 8, tipY - directionY * 11 + directionX * 8);
      context.moveTo(tipX, tipY);
      context.lineTo(tipX - directionX * 11 + directionY * 8, tipY - directionY * 11 - directionX * 8);
      context.stroke();
      context.globalAlpha = .8;
      context.fillStyle = '#8ea9e8';
      context.font = '700 15px "Noto Sans KR", sans-serif';
      context.textAlign = 'left';
      context.fillText(zone.label, centerX - 24, centerY + 44);
      context.restore();
    }
    // 반전 이벤트 존 — 예고 펄스 / 작동 틴트
    for (const [zoneIndex, eventZone] of spec.eventZones.entries()) {
      const scheduled = engine.events.find((event) => event.type === eventZone.type);
      if (!scheduled) continue;
      if (scheduled.focusZoneIndex !== undefined && scheduled.focusZoneIndex !== zoneIndex) continue;
      const announcing = engine.elapsed >= scheduled.announcesAt && engine.elapsed < scheduled.startsAt;
      const running = engine.elapsed >= scheduled.startsAt && engine.elapsed <= scheduled.startsAt + scheduled.duration;
      const fading = engine.elapsed > scheduled.startsAt + scheduled.duration
        && engine.elapsed < scheduled.startsAt + scheduled.duration + .6;
      if (!announcing && !running && !fading) continue;
      const pulse = announcing ? .45 + .35 * Math.sin(engine.elapsed * Math.PI * 2 * 1.2) : 1;
      const fade = fading ? 1 - (engine.elapsed - scheduled.startsAt - scheduled.duration) / .6 : 1;
      context.save();
      context.strokeStyle = engine.map.accent;
      context.fillStyle = engine.map.accent;
      context.lineWidth = 3;
      context.setLineDash([9, 7]);
      context.globalAlpha = (announcing ? .5 * pulse : .55) * fade;
      if (eventZone.rect) {
        context.strokeRect(eventZone.rect.x, eventZone.rect.y, eventZone.rect.w, eventZone.rect.h);
        if (running) {
          context.globalAlpha = .08 * fade;
          context.fillRect(eventZone.rect.x, eventZone.rect.y, eventZone.rect.w, eventZone.rect.h);
        }
      } else if (eventZone.circle) {
        context.beginPath();
        context.arc(eventZone.circle.x, eventZone.circle.y, eventZone.circle.r, 0, Math.PI * 2);
        context.stroke();
        if (running) {
          context.globalAlpha = .08 * fade;
          context.fill();
        }
      }
      context.restore();
    }
  }

  private drawFieldMarkers(engine: MarbleRaceEngine, spec: FieldSpec): void {
    const context = this.context;
    // 출발 표기 + 스타트 게이트
    const spawnCenter = spec.spawn.kind === 'rect'
      ? { x: spec.spawn.x + spec.spawn.w / 2, y: spec.spawn.y - 26 }
      : {
        x: spec.spawn.cx + Math.cos((spec.spawn.from + spec.spawn.to) / 2) * (spec.spawn.r + 66),
        y: spec.spawn.cy + Math.sin((spec.spawn.from + spec.spawn.to) / 2) * (spec.spawn.r + 66),
      };
    context.save();
    context.fillStyle = '#70e6d4';
    context.font = '800 17px "Lexend Deca", sans-serif';
    context.textAlign = 'center';
    context.fillText('START', spawnCenter.x, spawnCenter.y);
    context.restore();
    for (const gate of [spec.startGate, ...spec.extraStartGates ?? []]) {
      if (!gate || !fieldGateIsClosed(this.channelValue(engine, 'start-gate', 0))) continue;
      context.save();
      context.fillStyle = '#70e6d4';
      context.globalAlpha = .9;
      context.translate(gate.x + gate.w / 2, gate.y + gate.h / 2);
      context.rotate(gate.angle ?? 0);
      context.fillRect(-gate.w / 2, -gate.h / 2, gate.w, gate.h);
      context.restore();
    }
    if (spec.circuit) {
      context.save();
      for (const [index, path] of [spec.circuit.loop, spec.circuit.shortcut.path].entries()) {
        context.beginPath();
        path.forEach((point, i) => i === 0 ? context.moveTo(point.x, point.y) : context.lineTo(point.x, point.y));
        context.setLineDash([9, 17]);
        context.lineWidth = 2;
        context.strokeStyle = index === 0 ? 'rgba(255,255,255,.13)' : 'rgba(168,136,255,.38)';
        context.stroke();
      }
      context.restore();
    }
    // 결승 체커
    const finish = spec.finish;
    context.save();
    context.fillStyle = '#edf0ff';
    const cell = 11;
    if (finish.kind === 'rect') {
      const columns = finish.w >= finish.h ? Math.floor(finish.w / cell) : 2;
      const rows = finish.w >= finish.h ? 2 : Math.floor(finish.h / cell);
      const originX = finish.x + finish.w / 2 - columns * cell / 2;
      const originY = finish.y + finish.h / 2 - rows * cell / 2;
      for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columns; column += 1) {
          context.globalAlpha = (row + column) % 2 === 0 ? 1 : .25;
          context.fillRect(originX + column * cell, originY + row * cell, cell, cell);
        }
      }
    } else {
      for (let row = 0; row < 2; row += 1) {
        for (let column = 0; column < 2; column += 1) {
          context.globalAlpha = (row + column) % 2 === 0 ? 1 : .25;
          context.fillRect(finish.x - cell + column * cell, finish.y - cell + row * cell, cell, cell);
        }
      }
    }
    context.restore();
    // 랜드마크 라벨
    for (const landmark of spec.landmarks) {
      context.save();
      context.translate(landmark.x, landmark.y);
      context.fillStyle = 'rgba(7,8,24,.82)';
      context.strokeStyle = `${engine.map.accent}88`;
      context.lineWidth = 2;
      context.font = '700 12px "Noto Sans KR", sans-serif';
      const width = Math.max(76, context.measureText(landmark.label).width + 26);
      context.beginPath();
      context.roundRect(-width / 2, -14, width, 27, 8);
      context.fill();
      context.stroke();
      context.fillStyle = 'rgba(255,255,255,.78)';
      context.textAlign = 'center';
      context.fillText(landmark.label, 0, 5);
      context.restore();
    }
  }

  private channelValue(engine: MarbleRaceEngine, id: string, fallback = 0): number {
    return engine.spinnerAngle(id) ?? fallback;
  }

  private drawFieldObstacles(engine: MarbleRaceEngine, spec: FieldSpec, active: ActiveRaceEvent | null): void {
    const context = this.context;
    const accent = engine.map.accent;
    void active;
    for (const obstacle of spec.obstacles) {
      if (obstacle.kind === 'pin') {
        const raised = obstacle.sink ? this.channelValue(engine, 'pin-sink', 1) : 1;
        if (!fieldPinIsRaised(raised)) continue;
        context.save();
        context.globalAlpha = .75;
        context.fillStyle = '#8e97c4';
        context.beginPath();
        context.arc(obstacle.x, obstacle.y, obstacle.r, 0, Math.PI * 2);
        context.fill();
        context.restore();
      } else if (obstacle.kind === 'bumper') {
        context.save();
        context.translate(obstacle.x, obstacle.y);
        context.fillStyle = 'rgba(0,0,0,.35)';
        context.beginPath();
        context.arc(3, 5, obstacle.r, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = '#141b3e';
        context.strokeStyle = accent;
        context.lineWidth = 3;
        context.beginPath();
        context.arc(0, 0, obstacle.r, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.fillStyle = accent;
        context.globalAlpha = .85;
        context.beginPath();
        context.arc(0, 0, obstacle.r * .32, 0, Math.PI * 2);
        context.fill();
        context.restore();
      } else if (obstacle.kind === 'bar') {
        context.save();
        context.translate(obstacle.x, obstacle.y);
        context.rotate(obstacle.angle);
        context.fillStyle = FIELD_WALL;
        context.strokeStyle = FIELD_EDGE;
        context.lineWidth = 2;
        context.beginPath();
        context.roundRect(-obstacle.length / 2, -obstacle.thickness / 2, obstacle.length, obstacle.thickness, obstacle.thickness / 2);
        context.fill();
        context.stroke();
        context.restore();
      } else if (obstacle.kind === 'kicker') {
        const angle = this.channelValue(engine, obstacle.id, obstacle.restAngle);
        context.save();
        context.translate(obstacle.x, obstacle.y);
        context.rotate(angle);
        context.fillStyle = '#1a2248';
        context.strokeStyle = FIELD_OPEN;
        context.lineWidth = 3;
        context.beginPath();
        context.roundRect(0, -obstacle.thickness / 2, obstacle.length, obstacle.thickness, obstacle.thickness / 2);
        context.fill();
        context.stroke();
        context.fillStyle = FIELD_OPEN;
        context.beginPath();
        context.arc(obstacle.length, 0, obstacle.thickness * .72, 0, Math.PI * 2);
        context.fill();
        context.rotate(-angle);
        context.fillStyle = '#0a0f26';
        context.beginPath();
        context.arc(0, 0, 9, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.restore();
      } else if (obstacle.kind === 'paddle' || obstacle.kind === 'flipper') {
        const angle = this.channelValue(engine, obstacle.id, obstacle.kind === 'paddle' ? obstacle.phase : obstacle.restAngle);
        const length = obstacle.length;
        context.save();
        context.translate(obstacle.x, obstacle.y);
        context.rotate(angle);
        context.fillStyle = '#1a2248';
        context.strokeStyle = accent;
        context.lineWidth = 2.4;
        context.beginPath();
        context.roundRect(-length / 2, -obstacle.thickness / 2, length, obstacle.thickness, obstacle.thickness / 2);
        context.fill();
        context.stroke();
        context.rotate(-angle);
        context.fillStyle = '#0a0f26';
        context.beginPath();
        context.arc(0, 0, 9, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.restore();
      } else if (obstacle.kind === 'rotor') {
        const angle = this.channelValue(engine, obstacle.id);
        context.save();
        context.translate(obstacle.x, obstacle.y);
        context.rotate(angle);
        context.fillStyle = '#1a2248';
        context.strokeStyle = accent;
        context.lineWidth = 3;
        for (const arm of [0, Math.PI / 2]) {
          context.save();
          context.rotate(arm);
          context.beginPath();
          context.roundRect(-obstacle.wingLength / 2, -obstacle.thickness / 2, obstacle.wingLength, obstacle.thickness, obstacle.thickness / 2);
          context.fill();
          context.stroke();
          context.restore();
        }
        context.fillStyle = '#0a0f26';
        context.beginPath();
        context.arc(0, 0, obstacle.hubR, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.restore();
      } else if (obstacle.kind === 'disk') {
        const angle = this.channelValue(engine, obstacle.id);
        context.save();
        context.translate(obstacle.x, obstacle.y);
        context.fillStyle = '#1a2248';
        context.strokeStyle = accent;
        context.lineWidth = 3;
        context.beginPath();
        context.arc(0, 0, obstacle.r, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.rotate(angle);
        context.strokeStyle = `${accent}80`;
        context.lineWidth = 3;
        context.beginPath();
        for (const arm of [0, Math.PI / 2, Math.PI, Math.PI * 1.5]) {
          context.moveTo(Math.cos(arm) * (obstacle.r - 26), Math.sin(arm) * (obstacle.r - 26));
          context.lineTo(Math.cos(arm) * (obstacle.r - 6), Math.sin(arm) * (obstacle.r - 6));
        }
        context.stroke();
        context.strokeStyle = accent;
        context.lineWidth = 4;
        context.beginPath();
        context.arc(0, 0, obstacle.r * .82, -Math.PI / 2, 0);
        context.stroke();
        context.rotate(-angle);
        context.fillStyle = '#0a0f26';
        context.strokeStyle = accent;
        context.lineWidth = 2.4;
        context.beginPath();
        context.arc(0, 0, obstacle.hubR, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.restore();
      } else if (obstacle.kind === 'seesaw') {
        const angle = this.channelValue(engine, obstacle.id);
        context.save();
        context.translate(obstacle.x, obstacle.y);
        context.fillStyle = FIELD_EDGE;
        context.beginPath();
        context.moveTo(-18, 16);
        context.lineTo(0, -10);
        context.lineTo(18, 16);
        context.closePath();
        context.fill();
        context.rotate(angle);
        context.fillStyle = '#1a2248';
        context.strokeStyle = accent;
        context.lineWidth = 3;
        context.beginPath();
        context.roundRect(-obstacle.length / 2, -obstacle.thickness / 2, obstacle.length, obstacle.thickness, obstacle.thickness / 2);
        context.fill();
        context.stroke();
        context.rotate(-angle);
        context.fillStyle = '#0a0f26';
        context.beginPath();
        context.arc(0, 0, 8, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.restore();
      } else if (obstacle.kind === 'gate') {
        const openness = this.channelValue(engine, obstacle.id, 0);
        const vertical = obstacle.h >= obstacle.w;
        const closed = fieldGateIsClosed(openness);
        context.save();
        // 회전 게이트(나선 컷 등)는 중심 기준 로컬 좌표로 그린다
        context.translate(obstacle.x + obstacle.w / 2, obstacle.y + obstacle.h / 2);
        context.rotate(obstacle.angle ?? 0);
        const halfW = obstacle.w / 2;
        const halfH = obstacle.h / 2;
        if (obstacle.lapRule) {
          // 랩별 충돌 필터는 전역 고체 벽처럼 그리지 않는다. 통과 조건을 선 위에 명시한다.
          const entry = obstacle.lapRule === 'shortcut-entry';
          context.strokeStyle = entry && !closed ? FIELD_OPEN : accent;
          context.lineWidth = 3;
          context.setLineDash([6, 5]);
          context.beginPath();
          context.moveTo(vertical ? 0 : -halfW, vertical ? -halfH : 0);
          context.lineTo(vertical ? 0 : halfW, vertical ? halfH : 0);
          context.stroke();
          context.setLineDash([]);
          context.rotate(-(obstacle.angle ?? 0));
          context.fillStyle = '#0d1330';
          context.fillRect(-22, -8, 44, 16);
          context.fillStyle = entry && closed ? '#ffb9ae' : '#d6c8ff';
          context.font = '800 9px "Noto Sans KR", sans-serif';
          context.textAlign = 'center';
          context.fillText(obstacle.lapRule === 'lap-return' ? '1랩 ←' : obstacle.lapRule === 'shortcut-exit' ? '합류 ↓' : entry && closed ? '2랩 대기' : '2랩 ↓', 0, 3);
        } else if (closed) {
          context.fillStyle = FIELD_CLOSED;
          context.globalAlpha = .95;
          context.beginPath();
          context.roundRect(-halfW, -halfH, obstacle.w, obstacle.h, Math.min(obstacle.w, obstacle.h) / 2);
          context.fill();
        }
        if (!closed && !obstacle.lapRule) {
          context.strokeStyle = FIELD_OPEN;
          context.globalAlpha = .95;
          context.lineWidth = Math.min(10, vertical ? obstacle.w : obstacle.h);
          context.lineCap = 'round';
          context.beginPath();
          if (vertical) {
            context.moveTo(0, -halfH - 14);
            context.lineTo(0, -halfH - 4);
            context.moveTo(0, halfH + 4);
            context.lineTo(0, halfH + 14);
          } else {
            context.moveTo(-halfW - 14, 0);
            context.lineTo(-halfW - 4, 0);
            context.moveTo(halfW + 4, 0);
            context.lineTo(halfW + 14, 0);
          }
          context.stroke();
        }
        context.restore();
      } else if (obstacle.kind === 'shutter') {
        const openness = this.channelValue(engine, obstacle.id, 0);
        const closed = fieldGateIsClosed(openness);
        context.save();
        context.translate(obstacle.x, obstacle.y);
        context.fillStyle = '#080c20';
        context.strokeStyle = FIELD_EDGE;
        context.lineWidth = 5;
        context.beginPath();
        context.arc(0, 0, obstacle.r, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.lineWidth = 10;
        context.lineCap = 'round';
        // 상시 폐쇄 사분면 (SE·NW)
        context.strokeStyle = FIELD_CLOSED;
        context.beginPath();
        context.arc(0, 0, obstacle.r, 0, Math.PI / 2);
        context.stroke();
        context.beginPath();
        context.arc(0, 0, obstacle.r, Math.PI, Math.PI * 1.5);
        context.stroke();
        // 개폐 사분면 (NE·SW) — 열리면 민트로 바깥으로 벌어진다
        const gapRadius = obstacle.r + (closed ? 0 : 14);
        context.strokeStyle = closed ? FIELD_CLOSED : FIELD_OPEN;
        context.globalAlpha = closed ? .95 : 1;
        context.beginPath();
        context.arc(0, 0, gapRadius, -Math.PI / 2, 0);
        context.stroke();
        context.beginPath();
        context.arc(0, 0, gapRadius, Math.PI / 2, Math.PI);
        context.stroke();
        context.restore();
      } else if (obstacle.kind === 'lift') {
        const cycle = this.channelValue(engine, obstacle.id, obstacle.phase);
        const mouthOpen = this.channelValue(engine, `${obstacle.id}:gate`, 0) > .5;
        const pose = liftTravelPose(obstacle, cycle);
        context.save();
        // 샤프트 가이드
        context.strokeStyle = 'rgba(62,219,255,.3)';
        context.lineWidth = 2.4;
        context.setLineDash([6, 5]);
        context.beginPath();
        context.moveTo(obstacle.x - obstacle.width / 2, obstacle.crestSurfaceY - 4);
        context.lineTo(obstacle.x - obstacle.width / 2, obstacle.boardSurfaceY + 8);
        context.moveTo(obstacle.x + obstacle.width / 2, obstacle.crestSurfaceY - 4);
        context.lineTo(obstacle.x + obstacle.width / 2, obstacle.boardSurfaceY + 8);
        context.stroke();
        context.setLineDash([]);
        // 진행 화살표
        context.strokeStyle = 'rgba(62,219,255,.8)';
        context.lineWidth = 3;
        const arrowX = obstacle.x;
        const arrowTop = obstacle.crestSurfaceY + 46;
        const arrowBottom = Math.min(obstacle.boardSurfaceY - 40, arrowTop + 84);
        context.beginPath();
        context.moveTo(arrowX, arrowBottom);
        context.lineTo(arrowX, arrowTop);
        context.moveTo(arrowX, arrowTop);
        context.lineTo(arrowX - 8, arrowTop + 10);
        context.moveTo(arrowX, arrowTop);
        context.lineTo(arrowX + 8, arrowTop + 10);
        context.stroke();
        // 플랫폼
        context.translate(obstacle.x, pose.surfaceY + obstacle.thickness / 2);
        context.rotate(pose.angle);
        context.fillStyle = '#1a2248';
        context.strokeStyle = '#3edbff';
        context.lineWidth = 3.4;
        context.beginPath();
        context.roundRect(-obstacle.width / 2, -obstacle.thickness / 2, obstacle.width, obstacle.thickness, obstacle.thickness / 2);
        context.fill();
        context.stroke();
        context.restore();
        // 승차구 표시
        if (mouthOpen) {
          context.save();
          context.strokeStyle = FIELD_OPEN;
          context.lineWidth = 8;
          context.lineCap = 'round';
          context.globalAlpha = .9;
          for (const mouth of obstacle.mouths) {
            context.beginPath();
            context.moveTo(mouth.x + mouth.w / 2, mouth.y + 4);
            context.lineTo(mouth.x + mouth.w / 2, mouth.y + mouth.h * .4);
            context.stroke();
          }
          context.restore();
        }
        // 해치 (닫힘 상태)
        if (obstacle.hatch && !pose.hatchOpen) {
          context.save();
          context.fillStyle = FIELD_EDGE;
          context.globalAlpha = .9;
          context.beginPath();
          context.roundRect(obstacle.hatch.x, obstacle.hatch.y, obstacle.hatch.w, obstacle.hatch.h, 5);
          context.fill();
          context.restore();
        }
      }
    }
  }

  private drawTrack(map: FateMapDefinition, reduceMotion: boolean): void {
    const context = this.context;
    strokeVariableTrack(context, map, 14, 'rgba(0,0,0,.38)');
    strokeVariableTrack(context, map, 8, `${map.accent}a8`);
    strokeVariableTrack(context, map, 3, map.track);
    strokeVariableTrack(context, map, -9, 'rgba(8,10,30,.72)');

    pathToContext(context, map);
    context.lineDashOffset = reduceMotion ? 0 : -performance.now() * .018;
    context.setLineDash([16, 24]);
    context.strokeStyle = 'rgba(255,255,255,.15)';
    context.lineWidth = 2.5;
    context.stroke();
    context.setLineDash([]);
    context.lineDashOffset = 0;
  }

  private drawZones(map: FateMapDefinition, active: ActiveRaceEvent | null): void {
    const context = this.context;
    const samples = trackSamples(map.path);
    for (const zone of map.zones) {
      const selected = samples.filter((sample) => sample.progress >= zone.start && sample.progress <= zone.end);
      if (selected.length < 2) continue;
      context.beginPath();
      selected.forEach((point, index) => {
        if (index === 0) context.moveTo(point.x, point.y);
        else context.lineTo(point.x, point.y);
      });
      const relatedEvent = active && (
        active.event.type === zone.type
        || active.event.type === 'boost' && zone.type === 'finish'
      );
      context.globalAlpha = relatedEvent ? .24 + active.envelope * .18 : zone.type === 'collision' ? .07 : .12;
      context.strokeStyle = ZONE_COLORS[zone.type];
      context.lineWidth = zone.type === 'collision' ? 30 : 22;
      context.stroke();
      context.globalAlpha = 1;
    }
  }

  private drawLandmarks(map: FateMapDefinition): void {
    const context = this.context;
    for (const landmark of map.landmarks) {
      const pose = pointOnPath(map.path, landmark.progress);
      context.save();
      context.translate(pose.x, pose.y);
      context.fillStyle = 'rgba(7,8,24,.82)';
      context.strokeStyle = `${map.accent}88`;
      context.lineWidth = 2;
      context.beginPath();
      context.roundRect(-55, -pose.width / 2 - 36, 110, 25, 8);
      context.fill();
      context.stroke();
      context.fillStyle = 'rgba(255,255,255,.78)';
      context.font = '700 11px "Noto Sans KR", sans-serif';
      context.textAlign = 'center';
      context.fillText(landmark.label, 0, -pose.width / 2 - 19);
      context.restore();
    }
  }

  private drawObstacle(engine: MarbleRaceEngine, obstacle: MapObstacle, active: ActiveRaceEvent | null): void {
    const context = this.context;
    const map = engine.map;
    const pose = obstacleRenderPose(engine, obstacle, active);
    context.save();
    context.translate(pose.x, pose.y);
    if (obstacle.type === 'spinner') {
      context.rotate(pose.rotation);
      const arms = Math.max(1, Math.min(3, Math.round(obstacle.arms ?? 1)));
      const thickness = obstacle.thickness ?? obstacle.radius ?? 14;
      const halfLength = Math.max(thickness, (obstacle.length ?? 100) - thickness) / 2;
      for (let arm = 0; arm < arms; arm += 1) {
        context.save();
        context.rotate(arm * Math.PI / arms);
        context.strokeStyle = 'rgba(0,0,0,.4)';
        context.lineWidth = thickness + 7;
        context.beginPath();
        context.moveTo(-halfLength, 4);
        context.lineTo(halfLength, 4);
        context.stroke();
        context.strokeStyle = map.accent;
        context.lineWidth = thickness;
        context.beginPath();
        context.moveTo(-halfLength, 0);
        context.lineTo(halfLength, 0);
        context.stroke();
        context.restore();
      }
      context.fillStyle = '#fff4b0';
      context.beginPath();
      context.arc(0, 0, 10, 0, Math.PI * 2);
      context.fill();
    } else if (obstacle.type === 'gate') {
      const length = obstacle.length ?? 70;
      const halfThickness = obstacle.radius ?? 16;
      context.rotate(pose.angle + Math.PI / 2);
      context.fillStyle = map.accent;
      context.strokeStyle = '#fff7c5';
      context.lineWidth = 3;
      context.beginPath();
      context.roundRect(-length / 2, -halfThickness, length, halfThickness * 2, halfThickness * .7);
      context.fill();
      context.stroke();
    } else {
      const radius = obstacle.radius ?? 34;
      context.fillStyle = 'rgba(0,0,0,.35)';
      context.beginPath();
      context.arc(4, 6, radius, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = obstacle.type === 'peg' ? '#4fe0ca' : map.accent;
      context.strokeStyle = 'rgba(255,255,255,.8)';
      context.lineWidth = 5;
      context.beginPath();
      context.arc(0, 0, radius, 0, Math.PI * 2);
      context.fill();
      context.stroke();
      context.fillStyle = 'rgba(10,12,32,.4)';
      context.beginPath();
      context.arc(0, 0, radius * .45, 0, Math.PI * 2);
      context.fill();
    }
    context.restore();
  }

  private drawMotionTrail(map: FateMapDefinition, marble: RaceMarble): void {
    const speed = Math.hypot(marble.vx, marble.vy);
    if (speed < 68) return;
    const collisionZone = map.zones.some((zone) => zone.type === 'collision' && marble.progress >= zone.start && marble.progress <= zone.end);
    const context = this.context;
    const amount = Math.min(.16, .055 + (speed - 68) / 720);
    context.save();
    context.globalAlpha = Math.min(collisionZone ? .34 : .22, .08 + (speed - 68) / 320);
    context.strokeStyle = marble.color;
    context.lineWidth = collisionZone ? 4 : 3;
    context.beginPath();
    context.moveTo(marble.x - marble.vx * amount, marble.y - marble.vy * amount);
    context.lineTo(marble.x - marble.vx * .035, marble.y - marble.vy * .035);
    context.stroke();
    context.restore();
  }

  private drawCollisionEffects(engine: MarbleRaceEngine, reduceMotion: boolean, cameraZoom: number): void {
    const context = this.context;
    const maximum = 4;
    for (const effect of engine.collisionEffects().slice(-maximum)) {
      const amount = Math.max(0, Math.min(1, (engine.elapsed - effect.startsAt) / effect.duration));
      const alpha = 1 - amount;
      const scale = reduceMotion ? 1 : .7 + amount * .9;
      context.save();
      context.translate(effect.x, effect.y);
      if (effect.kind === 'bumper') {
        context.save();
        context.scale(1 / Math.max(.5, cameraZoom), 1 / Math.max(.5, cameraZoom));
        context.globalAlpha = alpha * .7;
        context.strokeStyle = engine.map.accent;
        context.lineWidth = 4;
        context.beginPath();
        context.arc(0, 0, 42 + amount * 26, 0, Math.PI * 2);
        context.stroke();
        context.restore();
      }
      context.globalAlpha = alpha;
      const screenScale = scale / Math.max(.5, cameraZoom);
      context.scale(screenScale, screenScale);
      const angle = Math.atan2(effect.dy, effect.dx);
      context.rotate(angle);
      context.lineCap = 'round';
      context.strokeStyle = '#fff';
      context.lineWidth = 4.5;
      context.beginPath();
      context.moveTo(-15, -15);
      context.lineTo(15, 15);
      context.moveTo(-15, 15);
      context.lineTo(15, -15);
      context.stroke();
      context.lineWidth = 4;
      context.strokeStyle = '#ff7a70';
      context.beginPath();
      context.moveTo(8, -4);
      context.lineTo(29 + effect.intensity * 8, -9);
      context.stroke();
      context.strokeStyle = '#ffd449';
      context.beginPath();
      context.moveTo(5, 7);
      context.lineTo(24 + effect.intensity * 7, 17);
      context.stroke();
      context.restore();
    }
  }

  private drawLine(map: FateMapDefinition, progress: number, label: string, color: string): void {
    const context = this.context;
    const pose = pointOnPath(map.path, progress);
    context.save();
    context.translate(pose.x, pose.y);
    context.rotate(pose.angle);
    context.fillStyle = color;
    const halfWidth = Math.max(32, pose.width / 2 - 7);
    context.fillRect(-7, -halfWidth, 14, halfWidth * 2);
    context.fillStyle = '#fff';
    context.font = '800 15px "Lexend Deca", sans-serif';
    context.textAlign = 'center';
    context.fillText(label, 0, -halfWidth - 12);
    context.restore();
  }

  private drawMarble(marble: RaceMarble, x: number, y: number, highlighted: boolean, result: boolean): void {
    const context = this.context;
    const radius = MARBLE_RADIUS;
    context.save();
    context.translate(x, y);
    if (result) {
      context.fillStyle = '#ffd166';
      context.font = '900 30px sans-serif';
      context.textAlign = 'center';
      context.fillText('★', 0, -22);
    }
    context.beginPath();
    context.arc(2, 3, radius, 0, Math.PI * 2);
    context.fillStyle = 'rgba(0,0,0,.3)';
    context.fill();
    context.beginPath();
    context.arc(0, 0, radius, 0, Math.PI * 2);
    context.fillStyle = marble.color;
    context.fill();
    context.strokeStyle = highlighted || result ? '#fff4a8' : 'rgba(255,255,255,.76)';
    context.lineWidth = highlighted || result ? 3 : 2;
    context.stroke();

    // 이름과 겹쳐 역슬래시처럼 보이던 회전 장식 대신, 글자 영역을 피한
    // 고정 광택만 둔다. 복제 번호와 무관하게 모든 마블의 이름이 동일하게 읽힌다.
    context.fillStyle = 'rgba(255,255,255,.34)';
    context.beginPath();
    context.arc(-5, -5, 2.1, 0, Math.PI * 2);
    context.fill();

    context.font = '900 8px "Noto Sans KR", sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    const fullName = marble.name.trim();
    let innerLabel = innerNameCache.get(fullName);
    if (!innerLabel) {
      innerLabel = context.measureText(fullName).width <= radius * 1.45
        ? fullName
        : abbreviatedMarbleName(fullName);
      innerNameCache.set(fullName, innerLabel);
    }
    context.drawImage(marbleNameSprite(innerLabel), -14, -9, 28, 18);
    context.restore();
  }

  private drawNameLabel(marble: RaceMarble, x: number, y: number, cameraZoom: number, labelIndex: number): void {
    const context = this.context;
    const text = marbleDisplayName(marble);
    context.save();
    context.translate(x, y);
    context.scale(1 / Math.max(.42, cameraZoom), 1 / Math.max(.42, cameraZoom));
    context.translate(labelIndex % 2 === 0 ? -18 : 18, -labelIndex * 24);
    context.font = '700 11px "Noto Sans KR", sans-serif';
    const width = Math.min(132, context.measureText(text).width + 18);
    context.fillStyle = 'rgba(10,10,28,.86)';
    context.beginPath();
    context.roundRect(-width / 2, -39, width, 22, 7);
    context.fill();
    context.fillStyle = '#fff';
    context.textAlign = 'center';
    context.fillText(text, 0, -24);
    context.restore();
  }

  private drawMiniMap(
    engine: MarbleRaceEngine,
    camera: CameraView,
    selection: CameraTargetSelection,
    inspecting: boolean,
  ): void {
    const context = this.context;
    const { compact, panel, content } = createMiniMapLayout(engine.map, this.width);
    const mapX = (worldX: number) => content.x + worldX / engine.map.worldWidth * content.width;
    const mapY = (worldY: number) => content.y + worldY / engine.map.worldHeight * content.height;

    context.save();
    context.fillStyle = 'rgba(7,8,24,.82)';
    context.beginPath();
    context.roundRect(panel.x, panel.y, panel.width, panel.height, 12);
    context.fill();
    if (inspecting) {
      context.strokeStyle = engine.map.accent;
      context.lineWidth = 1.5;
      context.stroke();
    }
    context.fillStyle = 'rgba(255,255,255,.62)';
    context.font = '700 8px "Noto Sans KR", sans-serif';
    context.fillText(
      inspecting ? '둘러보는 중' : compact ? '전체 트랙' : '전체 트랙 · 마우스로 이동',
      content.x,
      panel.y + 14,
    );

    context.save();
    context.beginPath();
    context.rect(content.x, content.y, content.width, content.height);
    context.clip();
    const cached = this.miniMapTrack;
    const trackWidth = content.width, trackHeight = content.height;
    if (!cached || cached.map !== engine.map || cached.width !== content.width || cached.height !== content.height || cached.compact !== compact) {
      const width = trackWidth, height = trackHeight;
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(width * 2);
      canvas.height = Math.ceil(height * 2);
      const context = canvas.getContext('2d')!;
      context.scale(2, 2);
      const content = { x: 0, y: 0, width, height };
      const mapX = (x: number) => x / engine.map.worldWidth * width;
      const mapY = (y: number) => y / engine.map.worldHeight * height;
      if (engine.map.field) {
        const layout = fieldLayoutPaths(engine.map.field);
        const scale = content.width / engine.map.worldWidth;
        context.save();
        context.translate(content.x, content.y);
        context.scale(scale, content.height / engine.map.worldHeight);
        context.fillStyle = 'rgba(255,255,255,.14)';
        context.strokeStyle = 'rgba(255,255,255,.14)';
        context.fill(layout.solids, layout.contour ? 'evenodd' : 'nonzero');
        context.lineCap = 'round';
        context.lineJoin = 'round';
        // 반투명 실루엣은 세그먼트를 이어 한 번에 칠한다 — 개별 스트로크는
        // 겹치는 둥근 캡마다 알파가 중첩돼 곡선 파이프가 구슬처럼 끊겨 보인다.
        let chainWidth = -1;
        let chainEndX = 0;
        let chainEndY = 0;
        let chainOpen = false;
        const flushChain = () => {
          if (chainOpen) context.stroke();
          chainOpen = false;
        };
        for (const segment of layout.pipeSegments) {
          const continues = chainOpen
            && Math.abs(segment.width - chainWidth) < .5
            && Math.hypot(segment.x0 - chainEndX, segment.y0 - chainEndY) < .5;
          if (!continues) {
            flushChain();
            context.lineWidth = segment.width;
            chainWidth = segment.width;
            context.beginPath();
            context.moveTo(segment.x0, segment.y0);
            chainOpen = true;
          }
          context.lineTo(segment.x1, segment.y1);
          chainEndX = segment.x1;
          chainEndY = segment.y1;
        }
        flushChain();
        context.fillStyle = 'rgba(7,8,24,.85)';
        context.fill(layout.holes);
        const finish = engine.map.field.finish;
        context.fillStyle = '#edf0ff';
        const finishX = finish.kind === 'circle' ? finish.x : finish.x + finish.w / 2;
        const finishY = finish.kind === 'circle' ? finish.y : finish.y + finish.h / 2;
        const cell = 5 / scale;
        context.globalAlpha = 1;
        context.fillRect(finishX - cell, finishY - cell, cell, cell);
        context.fillRect(finishX, finishY, cell, cell);
        context.globalAlpha = .3;
        context.fillRect(finishX, finishY - cell, cell, cell);
        context.fillRect(finishX - cell, finishY, cell, cell);
        context.restore();
      } else {
        context.beginPath();
        trackSamples(engine.map.path).forEach((point, index) => {
          if (index === 0) context.moveTo(mapX(point.x), mapY(point.y));
          else context.lineTo(mapX(point.x), mapY(point.y));
        });
        context.strokeStyle = 'rgba(255,255,255,.26)';
        context.lineWidth = compact ? 5 : 7;
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.stroke();
      }

      this.miniMapTrack = { map: engine.map, width, height, compact, canvas };
    }
    context.drawImage(this.miniMapTrack!.canvas, content.x, content.y, content.width, content.height);

    const worldViewWidth = this.width / camera.zoom;
    const worldViewHeight = this.height / camera.zoom;
    context.strokeStyle = 'rgba(255,255,255,.48)';
    context.lineWidth = 1;
    context.strokeRect(
      mapX(camera.x - worldViewWidth / 2),
      mapY(camera.y - worldViewHeight / 2),
      worldViewWidth / engine.map.worldWidth * content.width,
      worldViewHeight / engine.map.worldHeight * content.height,
    );

    const highlighted = new Set(selection.highlightedIds);
    for (const marble of marblesStillOnTrack(engine.marbles)) {
      context.beginPath();
      context.arc(mapX(marble.x), mapY(marble.y), highlighted.has(marble.id) ? 3 : 1.8, 0, Math.PI * 2);
      context.fillStyle = marble.color;
      context.fill();
      if (highlighted.has(marble.id)) {
        context.strokeStyle = '#fff';
        context.lineWidth = 1;
        context.stroke();
      }
    }
    context.restore();
    context.restore();
  }
}
